import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Link as RouterLink } from 'react-router-dom';
import { slugify } from '../utils/slugify';
import { Box, Typography, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper, Button } from '@mui/material';
import { Helmet } from 'react-helmet-async';

// Pomocnicza funkcja do formatowania daty, zachowana z oryginalnego kodu
const convertUTCToLocal = (utcValue) => {
  if (!utcValue) return '';
  let utcDate = new Date(utcValue.replace(' ', 'T') + 'Z');
  return utcDate.toLocaleString('pl-PL', { timeZone: 'Europe/Warsaw' });
};

// Przywrócony oryginalny gradient i cieniowanie z pliku PositionMatrix.js
const getGradientColor = (value) => {
  if (value === 0) return 'rgba(255, 255, 255, 0.3)'; // Pojaśniony neutralny szary
  const scaledValue = Math.max(0, value / 0.8);
  const grayBase = 255;
  const r = Math.round(grayBase * (1 - scaledValue));
  const g = Math.round(grayBase * (1 - scaledValue) + 255 * scaledValue);
  const b = Math.round(grayBase * (1 - scaledValue) + 50 * scaledValue); // Lekkie niebieskie dla głębi
  const alpha = 0.3 + scaledValue * 0.7;
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
};

const WcPositionMatrix = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeGroup, setActiveGroup] = useState('A');

  useEffect(() => {
    setLoading(true);
    axios.get(`${process.env.REACT_APP_API_URL}/api/simulation/worldcup/latest-groups`)
      .then(response => {
        setData(response.data);
        setLoading(false);
      })
      .catch(err => {
        console.error("Błąd ładowania szans grupowych MŚ:", err);
        setError("Nie udało się pobrać danych symulacji.");
        setLoading(false);
      });
  }, []);

  const groupsList = data?.teams 
    ? Array.from(new Set(data.teams.map(t => t.groupIdentifier))).sort()
    : [];

  const filteredTeams = data?.teams
    ? data.teams.filter(t => t.groupIdentifier === activeGroup)
    : [];

  if (loading) {
    return (
      <Box sx={{ p: 3, textAlign: 'center' }}>
        <Typography variant="h6">Ładowanie macierzy szans grup MŚ...</Typography>
      </Box>
    );
  }

  if (error || !data) {
    return (
      <Box sx={{ p: 3, textAlign: 'center' }}>
        <Typography variant="h6" color="error">{error || "Brak danych"}</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ width: '100%', p: { xs: 1, sm: 2 } }}>
      <Helmet>
        <title>Szansa na miejsca w grupach - Mistrzostwa Świata | Symuliga</title>
      </Helmet>

      {/* Tytuł nagłówka (bez numeru simulationId) */}
      <Box sx={{ mb: 3, textAlign: 'center' }}>
        <Typography variant="h5" component="h1" sx={{ fontWeight: 'bold', mb: 0.5 }}>
          Mistrzostwa Świata - Szanse na miejsca w grupach
        </Typography>
        <Typography variant="caption" color="text.secondary">
          Data symulacji: {convertUTCToLocal(data.simulationDateTime)}
        </Typography>
      </Box>

      {/* Przyciski grup - szerokość dopasowana do zawartości i wyśrodkowana */}
      <Box sx={{ 
        display: 'flex', 
        flexWrap: 'wrap', 
        justifyContent: 'center', 
        gap: 1, 
        mb: 3,
        p: 1,
        backgroundColor: 'background.paper',
        borderRadius: 1,
        boxShadow: 1,
        width: 'fit-content',
        margin: '0 auto 24px auto'
      }}>
        {groupsList.map((groupId) => (
          <Button
            key={groupId}
            variant={activeGroup === groupId ? "contained" : "outlined"}
            onClick={() => setActiveGroup(groupId)}
            sx={{
              minWidth: { xs: '40px', sm: '50px' },
              fontWeight: 'bold',
              fontSize: { xs: '0.8rem', sm: '0.9rem' },
              p: { xs: '4px 8px', sm: '6px 12px' }
            }}
          >
            {groupId}
          </Button>
        ))}
      </Box>

      {/* Węższy kontener tabeli (maksymalnie 1000px, wyśrodkowany) wzorowany na oryginalnej strukturze */}
      <Box sx={{ maxWidth: '1000px', margin: '0 auto' }}>
        <TableContainer
          component={Paper}
          sx={{
            marginBottom: 1,
            margin: '0 auto',
            width: 'auto',
            maxWidth: 'fit-content', // Ogranicza rozszerzanie tabeli
            overflowX: 'auto',
            position: 'relative',
            borderRadius: '12px',
            WebkitOverflowScrolling: 'touch',
            '&::-webkit-scrollbar': { width: '8px', height: '8px' },
            '&::-webkit-scrollbar-thumb': { backgroundColor: 'rgba(0,0,0,0.3)', borderRadius: '4px' },
            '&::before': {
              content: '"SymuLiga.pl"',
              position: 'absolute',
              top: '60%',
              left: '50%',
              transform: 'translate(-50%, -50%) rotate(-45deg)',
              fontSize: { xs: '3rem', sm: '4rem', md: '5rem' },
              fontWeight: 'bold',
              color: 'rgba(0, 0, 0, 0.15)',
              pointerEvents: 'none',
              zIndex: 0,
              whiteSpace: 'nowrap',
            },
          }}
        >
          <Table size="small" sx={{ width: 'auto', tableLayout: 'auto', borderCollapse: 'collapse' }} aria-label="world cup groups probability matrix table">
            <TableHead>
              <TableRow>
                <TableCell
                  sx={{
                    position: 'sticky',
                    left: 0,
                    top: 0,
                    zIndex: 3,
                    backgroundColor: 'background.paper',
                    width: 'auto',
                    maxWidth: '100%',
                    whiteSpace: 'normal',
                    padding: '6px',
                    fontSize: { xs: '0.8rem', sm: '0.9rem' },
                    borderRight: '1px solid rgba(224, 224, 224, 1)',
                  }}
                >
                  Drużyna (Grupa {activeGroup})
                </TableCell>
                <TableCell align="center" sx={{ position: 'sticky', top: 0, zIndex: 2, backgroundColor: 'background.paper', width: 'auto', padding: '6px', fontSize: { xs: '0.8rem', sm: '0.9rem' }, borderBottom: '2px solid rgba(224, 224, 224, 1)' }}>1. miejsce</TableCell>
                <TableCell align="center" sx={{ position: 'sticky', top: 0, zIndex: 2, backgroundColor: 'background.paper', width: 'auto', padding: '6px', fontSize: { xs: '0.8rem', sm: '0.9rem' }, borderBottom: '2px solid rgba(224, 224, 224, 1)' }}>2. miejsce</TableCell>
                <TableCell align="center" sx={{ position: 'sticky', top: 0, zIndex: 2, backgroundColor: 'background.paper', width: 'auto', padding: '6px', fontSize: { xs: '0.8rem', sm: '0.9rem' }, borderBottom: '2px solid rgba(224, 224, 224, 1)' }}>3. miejsce</TableCell>
                <TableCell align="center" sx={{ position: 'sticky', top: 0, zIndex: 2, backgroundColor: 'background.paper', width: 'auto', padding: '6px', fontSize: { xs: '0.8rem', sm: '0.9rem' }, borderBottom: '2px solid rgba(224, 224, 224, 1)' }}>4. miejsce</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredTeams.map((row, rowIndex) => {
                const probabilities = [row.pctPos1, row.pctPos2, row.pctPos3, row.pctPos4];

                return (
                  <TableRow
                    key={rowIndex}
                    // Usunięto '&:hover': { backgroundColor: 'action.hover' } aby nie psuć efektu powiększania komórek
                  >
                    {/* Nazwa drużyny jako link */}
                    <TableCell
                     
                      sx={{
							  position: 'sticky',
							  left: 0,
							  zIndex: 3,
							  backgroundColor: 'background.paper',
							  width: 'auto', // Dopasowanie do zawartości
							  minWidth: 40, // Minimalna szerokość dostosowana
							  padding: '5px',
							  fontSize: { xs: '0.7rem', sm: '0.8rem' },
							  borderRight: '1px solid rgba(224, 224, 224, 1)',
							}}
                    >
                      {row.teamName}
                    </TableCell>

                    {/* Komórki szans z oryginalnymi paddingami i cieniem czcionki textShadow */}
                    {probabilities.map((prob, colIndex) => (
                      <TableCell
                        key={colIndex}
                        align="center"
                        sx={{
								width: 'auto', // Proporcjonalne dostosowanie
								minWidth: 70, // Minimalna szerokość dostosowana
								backgroundColor: getGradientColor(prob),
								color: 'black', // Zawsze czarny dla awansu
								fontWeight: 'bold',
								fontSize: { xs: '0.8rem', sm: '0.9rem' },
								padding: '5px',
								textShadow: '0.5px 0.5px 1px rgba(0, 0, 0, 0.3)',
								transition: 'background-color 0.3s',
								'@media (hover: hover)': {
								  '&:hover': {
									backgroundColor: getGradientColor(Math.min(prob + 0.1, 1)),
									transform: 'scale(1.05)',
								  },
								},
							  }}
                      >
                        {prob > 0 ? `${(prob * 100).toFixed(1)}%` : '-'}
                      </TableCell>
                    ))}
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      </Box>
    </Box>
  );
};

export default WcPositionMatrix;